import { useCallback, useEffect, useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import MenuWizard from './MenuWizard';
import MenuPage from './MenuPage';
import Sidebar from '../../components/Sidebar';
import { setupApiClientInterceptors } from '../../api/apiClient';
import { useAuthContext } from '../../contexts/AuthContext';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import NotFoundPage from '../../components/NotFoundPage';
import useResponsive from '../../hooks/useResponsive';
import {
  PAGE_CREATE_DRINK_ITEM,
  PAGE_EDIT_DRINK_ITEM,
  PAGE_CREATE_MENU,
  PAGE_EDIT_MENU,
  PAGE_MENUS,
  PAGE_SETTINGS,
  PAGE_SETTINGS_ACCOUNT,
  PAGE_CREATE_MENU_ITEM,
  PAGE_EDIT_MENU_ITEM,
  PAGE_CREATE_RESTAURANT,
  PAGE_EDIT_RESTAURANT,
  PAGE_PROMOTIONS,
  PAGE_RESERVATION_LINKS,
  PAGE_ONLINE_ORDERING,
  PAGE_EMAIL_MANAGER,
  PAGE_CREATE_MODIFIER,
  PAGE_EDIT_MODIFIER,
  PAGE_MODIFIERS,
  PAGE_PRESS,
  PAGE_ABOUT,
  PAGE_MEDIA_LIBRARY,
  PAGE_DISCOVERY_CONTENT,
  PAGE_CREATE_CONTENT,
  PAGE_EDIT_CONTENT,
  PAGE_PAGES,
  PAGE_ADD_PAGE,
  PAGE_NEW_PAGE,
  PAGE_CUSTOM_PAGE,
  PAGE_EVENTS,
  PAGE_STRIPE_CONNECT_ONBOARDING_SUCCESS,
  PAGE_DESIGN,
  PAGE_DESIGN_COLORS,
  PAGE_DESIGN_FONTS,
  PAGE_MENUS_QR_CODE,
  PAGE_MENUS_PAPER,
  PAGE_PRINTING_MATERIALS,
  PAGE_COMING_SOON,
  PAGE_BRAND_PROFILE,
  PAGE_LOCATIONS,
  PAGE_ADD_LOCATION,
  PAGE_ADD_BRAND
} from '../../constants/UriConstants';
import MenuItemWizard from './MenuItemWizard';
import ModifierWizard from './ModifierWizard';
import Settings from './Settings';
import MenuListView from '../../components/MenuListView';
import CharacteristicsProvider from '../../contexts/CharacteristicsContext';
import 'react-toastify/dist/ReactToastify.css';
import MenuLayoutProvider from '../../contexts/MenuLayoutContext';
import TagsProvider from '../../contexts/TagsContext';
import DrinkItemsProvider from '../../contexts/DrinkItemsContext';
import ManagerProvider from '../../contexts/ManagerContext';
import RestaurantWizard from './RestaurantWizard';
import CuisinesProvider from '../../contexts/CuisinesContext';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import AnnouncementsProvider from '../../contexts/AnnouncementsContext';
import PromotionsPage from './PromotionsPage';
import AlertModal from '../../components/common/AlertModal';
import ReservationOrderPage from './ReservationOrderPage';
import OnlineOrderingPage from './OnlineOrderingPage/OnlineOrderingPage';
import EmailManagerPage from './EmailManagerPage';
import SubmittedEmailsProvider from '../../contexts/SubmittedEmailsContext';
import ErrorModal from '../../components/ErrorModal';
import ModifiersView from '../../components/ModifiersView';
import ModifiersProvider from '../../contexts/ModifiersContext';
import MediaLibrary from './MediaLibrary';
import MobileNavBar from '../../components/MobileNavBar';
import DiscoveryContentPage from './DiscoveryContentPage';
import DiscoveryContentWizard from './DiscoveryContentWizard/DiscoveryContentWizard';
import DiscoveryContentItemsProvider from '../../contexts/DiscoveryContentItemsContext/DiscoveryContentItemsContext';
import PressPage from './PressPage/PressPage';
import AboutPage from './AboutPage';
import AddPageTemplatePage from './AddPageTemplatePage';
import CustomPageEditor from './CustomPageEditor';
import PagesPage from './PagesPage';
import EventsPage from './EventsPage';
import EventSettingsProvider from '../../contexts/EventSettingsContext';
import StripeOnboardingSuccessPage from '../StripeOnboardingSuccessPage/StripeOnboardingSuccessPage';
import DesignPage from './DesignPage';
import PrintingMaterialsPage from './PrintingMaterialsPage';
import ComingSoonPage from './ComingSoonPage';
import LocationsPage from './LocationsPage';
import LocationWizard from './LocationWizard';
import LocationBasicInfoPage from './LocationProfile/LocationBasicInfoPage';
import BrandWizard from './BrandWizard';
import BrandEditor from './BrandEditor';
import LocationProfilePage from './LocationProfile/LocationProfilePage';
import LocationAddressPage from './LocationProfile/LocationAddressPage';
import LocationHoursPage from './LocationProfile/LocationHoursPage';
import { useBrandContext } from '../../contexts/BrandContext';

const RootRedirect = () => {
  const { currentBrand, isLoading } = useBrandContext();
  if (isLoading) return null;
  if (currentBrand) return <Navigate to={`/${currentBrand.id}/brand-profile`} replace />;
  return null;
};

const MainContainer = () => {
  const { isDesktop } = useResponsive();
  const { removeToken } = useAuthContext();
  const { clearUserPermissions } = usePermissionsContext();
  const [showError, setShowError] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const dismissErrorModal = () => {
    setShowError(false);
  };
  const showErrorModal = useCallback((message: string) => {
    setErrorModalMessage(message);
    setShowError(true);
  }, []);

  const handleForcedLogout = useCallback(() => {
    removeToken();
    clearUserPermissions();
  }, [removeToken, clearUserPermissions]);

  useEffect(() => {
    setupApiClientInterceptors(handleForcedLogout, showErrorModal);
  }, [handleForcedLogout, showErrorModal]);

  return (
    <div className={`app-shell ${isDesktop ? 'flex' : 'flex-col'}`}>
      {showError && (
        <ErrorModal modalTitle="Network Error" modalMessage={errorModalMessage} onDismiss={dismissErrorModal} />
      )}
      <LoadingOverlay />
      <AlertModal />
      {isDesktop ? <Sidebar /> : <MobileNavBar />}
      <main className="app-main">
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route
            path={`/:brandId/${PAGE_BRAND_PROFILE}/*`}
            element={
              <CuisinesProvider>
                <BrandEditor />
              </CuisinesProvider>
            }
          />
          <Route
            path={`/:brandId/${PAGE_ADD_BRAND}`}
            element={
              <CuisinesProvider>
                <BrandWizard />
              </CuisinesProvider>
            }
          />
          <Route path="/location-profile/:restaurantId" element={<LocationProfilePage />} />
          <Route path="/:restaurantID/basic-information" element={<LocationBasicInfoPage />} />
          <Route path="/:restaurantID/address" element={<LocationAddressPage />} />
          <Route path="/:restaurantID/hours" element={<LocationHoursPage />} />
          <Route path={`/${PAGE_PAGES}`} element={<PagesPage />} />
          <Route path={`/${PAGE_ADD_PAGE}`} element={<AddPageTemplatePage />} />
          <Route path={`/${PAGE_NEW_PAGE}/:template`} element={<CustomPageEditor key="new-page" />} />
          <Route path={`/${PAGE_CUSTOM_PAGE}/:pageID`} element={<CustomPageEditor key="edit-page" />} />
          <Route
            path={`/${PAGE_EVENTS}`}
            element={
              <EventSettingsProvider>
                <EventsPage />
              </EventSettingsProvider>
            }
          />
          <Route path={`/${PAGE_DESIGN}`} element={<Navigate to={`/${PAGE_DESIGN_COLORS}`} replace />} />
          <Route path={`/${PAGE_DESIGN_COLORS}`} element={<DesignPage />} />
          <Route path={`/${PAGE_DESIGN_FONTS}`} element={<DesignPage />} />
          <Route path={`/${PAGE_MEDIA_LIBRARY}`} element={<MediaLibrary />} />
          <Route path={`/${PAGE_PRINTING_MATERIALS}`} element={<PrintingMaterialsPage />} />
          <Route path={`/${PAGE_COMING_SOON}`} element={<ComingSoonPage />} />
          <Route path={`/${PAGE_MENUS_QR_CODE}`} element={<MenuPage />} />
          <Route path={`/${PAGE_MENUS_PAPER}`} element={<MenuPage />} />
          <Route path={`/${PAGE_ABOUT}`} element={<AboutPage />} />
          <Route path={`/${PAGE_PRESS}`} element={<PressPage />} />
          <Route
            path={`/${PAGE_PROMOTIONS}`}
            element={
              <AnnouncementsProvider>
                <PromotionsPage />
              </AnnouncementsProvider>
            }
          />
          <Route
            path={`/${PAGE_DISCOVERY_CONTENT}`}
            element={
              <DiscoveryContentItemsProvider>
                <DiscoveryContentPage />
              </DiscoveryContentItemsProvider>
            }
          />
          <Route
            path={`/${PAGE_EMAIL_MANAGER}`}
            element={
              <SubmittedEmailsProvider>
                <EmailManagerPage />
              </SubmittedEmailsProvider>
            }
          />
          <Route
            path={`/${PAGE_SETTINGS_ACCOUNT}`}
            element={
              <MenuLayoutProvider>
                <ManagerProvider>
                  <Settings />
                </ManagerProvider>
              </MenuLayoutProvider>
            }
          />
          <Route
            path={`/${PAGE_SETTINGS}`}
            element={
              <MenuLayoutProvider>
                <ManagerProvider>
                  <Settings />
                </ManagerProvider>
              </MenuLayoutProvider>
            }
          />
          <Route path="/:restaurantID" element={<MenuPage />}>
            <Route
              path={PAGE_MENUS}
              element={
                <DrinkItemsProvider>
                  <CharacteristicsProvider>
                    <ModifiersProvider>
                      <MenuListView />
                    </ModifiersProvider>
                  </CharacteristicsProvider>
                </DrinkItemsProvider>
              }
            />
            <Route
              path={PAGE_MODIFIERS}
              element={
                <ModifiersProvider>
                  <ModifiersView />
                </ModifiersProvider>
              }
            />
          </Route>
          <Route
            path={PAGE_CREATE_DRINK_ITEM}
            element={
              <DrinkItemsProvider>
                <CharacteristicsProvider>
                  <TagsProvider>
                    <ModifiersProvider>
                      <MenuItemWizard key="create-drink-item" />
                    </ModifiersProvider>
                  </TagsProvider>
                </CharacteristicsProvider>
              </DrinkItemsProvider>
            }
          />
          <Route
            path={PAGE_EDIT_DRINK_ITEM}
            element={
              <DrinkItemsProvider>
                <CharacteristicsProvider>
                  <TagsProvider>
                    <ModifiersProvider>
                      <MenuItemWizard key="edit-drink-item" />
                    </ModifiersProvider>
                  </TagsProvider>
                </CharacteristicsProvider>
              </DrinkItemsProvider>
            }
          />
          <Route path={PAGE_CREATE_MENU} element={<MenuWizard />} />
          <Route path={PAGE_EDIT_MENU} element={<MenuWizard />} />
          <Route
            path={PAGE_CREATE_CONTENT}
            element={
              <DiscoveryContentItemsProvider>
                <DiscoveryContentWizard key="create-content" />
              </DiscoveryContentItemsProvider>
            }
          />
          <Route
            path={PAGE_EDIT_CONTENT}
            element={
              <DiscoveryContentItemsProvider>
                <DiscoveryContentWizard key="edit-content" />
              </DiscoveryContentItemsProvider>
            }
          />
          <Route
            path={PAGE_CREATE_MENU_ITEM}
            element={
              <DrinkItemsProvider>
                <CharacteristicsProvider>
                  <TagsProvider>
                    <ModifiersProvider>
                      <MenuItemWizard key="create-menu-item" />
                    </ModifiersProvider>
                  </TagsProvider>
                </CharacteristicsProvider>
              </DrinkItemsProvider>
            }
          />
          <Route
            path={PAGE_EDIT_MENU_ITEM}
            element={
              <DrinkItemsProvider>
                <CharacteristicsProvider>
                  <TagsProvider>
                    <ModifiersProvider>
                      <MenuItemWizard key="edit-menu-item" />
                    </ModifiersProvider>
                  </TagsProvider>
                </CharacteristicsProvider>
              </DrinkItemsProvider>
            }
          />
          <Route
            path={PAGE_CREATE_MODIFIER}
            element={
              <ModifiersProvider>
                <ModifierWizard />
              </ModifiersProvider>
            }
          />
          <Route
            path={PAGE_EDIT_MODIFIER}
            element={
              <ModifiersProvider>
                <ModifierWizard />
              </ModifiersProvider>
            }
          />
          <Route
            path={`${PAGE_CREATE_RESTAURANT}/*`}
            element={
              <MenuLayoutProvider>
                <CuisinesProvider>
                  <RestaurantWizard />
                </CuisinesProvider>
              </MenuLayoutProvider>
            }
          />
          <Route
            path={`${PAGE_EDIT_RESTAURANT}/*`}
            element={
              <MenuLayoutProvider>
                <CuisinesProvider>
                  <RestaurantWizard />
                </CuisinesProvider>
              </MenuLayoutProvider>
            }
          />
          <Route path={`/:restaurantID/${PAGE_RESERVATION_LINKS}`} element={<ReservationOrderPage />} />
          <Route path={`/:restaurantID/${PAGE_ONLINE_ORDERING}`} element={<OnlineOrderingPage />} />
          <Route path={`/${PAGE_STRIPE_CONNECT_ONBOARDING_SUCCESS}`} element={<StripeOnboardingSuccessPage />} />
          <Route path={`/:brandId/${PAGE_LOCATIONS}`} element={<LocationsPage />} />
          <Route path={`/:brandId/${PAGE_ADD_LOCATION}`} element={<LocationWizard />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default MainContainer;
